import { useMemo, useRef } from "react";
import {
  AreaChart, Area, 
  BarChart, Bar, 
  LineChart, Line, 
  PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import * as htmlToImage from "html-to-image";

export type ChartSpec = {
  type: "line" | "bar" | "area" | "pie" | "radar";
  title?: string;
  xKey: string;
  yKeys: string[];
  seriesNames?: string[];
  groupBy?: string | null;
  options?: { stacked?: boolean; xTickFormat?: "date"|"number"|"string"; yTickFormat?: "number"|"currency"|"percent"; smooth?: boolean; legend?: boolean; };
};

const ChartRenderer = ({ spec, data }: { spec: ChartSpec; data: any[] }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { type, xKey, yKeys, title = "My Chart" } = spec;

  const columns = useMemo(() => {
    return [xKey, ...yKeys];
  }, [xKey, yKeys]);

  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#ff0000', '#0076bf'];
  const names = spec.seriesNames?.length === spec.yKeys.length ? spec.seriesNames : spec.yKeys;
  const Axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey={spec.xKey} />
      <YAxis />
      <Tooltip  cursor={{ fill: "#626479", opacity: 0.4 }} contentStyle={{ backgroundColor: "#222", color: "#fff", border: "1px solid #444", borderRadius: "12px" }} />
      {spec.options?.legend !== false && <Legend />}
    </>
  );

  // Handle exporting chart data & chart image to Excel
  const handleExport = async () => {
    if (!data?.length) return;
    try {
      // 1) Capture the chart node (SVG) as a PNG data URL using html-to-image
      let dataUrl: string | undefined;
      if (chartRef.current) {
        dataUrl = await htmlToImage.toPng(chartRef.current, {
          pixelRatio: 2,
          skipFonts: true,
          cacheBust: true,
        });
      }

      // 2) Build the workbook with ExcelJS
      const wb = new ExcelJS.Workbook();
      wb.creator = "ALLY";
      wb.created = new Date();

      // 2a) Data sheet
      const wsData = wb.addWorksheet("Data");
      // Header row
      wsData.addRow(columns);
      // Body rows
      data.forEach((row: any) => {
        wsData.addRow(columns.map((col) => row[col] as any));
      });

      // Auto-width
      columns.forEach((_, idx) => {
        const maxLen = Math.max(
          ...wsData.getColumn(idx + 1)
            .values
            .filter((v) => v != null)
            .map((v) => String(v).length)
        );
        wsData.getColumn(idx + 1).width = Math.min(40, Math.max(10, maxLen + 2));
      });

      // 2b) Chart sheet with embedded PNG
      const wsChart = wb.addWorksheet("Chart");
      if (dataUrl) {
        const imageId = wb.addImage({ base64: dataUrl, extension: "png" });
        wsChart.addImage(imageId, {
          tl: { col: 0, row: 2 },
          ext: { width: 1200, height: 600 },
          editAs: "oneCell",
        });
      }

      // 3) Generate a Blob and download with file-saver
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, "chart_data.xlsx");
    } catch (err) {
      console.error("Excel export failed:", err);
    }
  }

  return (
    <div className="my-0">
      {spec.title && <h3 className="mb-2 text-lg font-semibold">{spec.title}</h3>}
      <div className="h-96" ref={chartRef}>
        <ResponsiveContainer width="100%" height="100%">
          <>
            {spec.type === "line" && (
              <LineChart height={360} width={750} data={data}>
                {Axes}
                {spec.yKeys.map((k,i)=>(<Line key={k} dataKey={k} name={names[i]} type={spec.options?.smooth ? "monotone" : "linear"} stroke={colors[i % colors.length]} strokeWidth={3} dot={false} isAnimationActive={false}/>))}
              </LineChart>
            )}
            {spec.type === "bar" && (
              <BarChart height={360} width={750} data={data}>
                {Axes}
                {spec.yKeys.map((k,i)=>(<Bar key={k} dataKey={k} name={names[i]} stackId={spec.options?.stacked ? "s" : undefined} fill={colors[i % colors.length]} activeBar={{ fill: "#1976d2" }} isAnimationActive={false}/>))}
              </BarChart>
            )}
            {spec.type === "area" && (
              <AreaChart height={360} width={750} data={data}>
                {Axes}
                {spec.yKeys.map((k,i)=>(<Area key={k} dataKey={k} name={names[i]} type={spec.options?.smooth ? "monotone" : "linear"} fill={colors[i % colors.length]} isAnimationActive={false}/>))}
              </AreaChart>
            )}
            {spec.type === "pie" && (
              <PieChart height={360} width={700}>
                <Tooltip/><Legend/>
                <Pie data={data} dataKey={spec.yKeys[0]} nameKey={spec.xKey} label isAnimationActive={false}>
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Pie>
              </PieChart>
            )}
            {spec.type === "radar" && (
              <RadarChart outerRadius={90} width={730} height={250} data={data}>
                <PolarGrid />
                <PolarAngleAxis dataKey={spec.xKey} />
                <PolarRadiusAxis />
                <Tooltip />
                <Legend />
                {spec.yKeys.map((k,i)=>(<Radar key={k} dataKey={k} name={names[i]} fill={colors[i % colors.length]} stroke={colors[i % colors.length]} isAnimationActive={false}/>))}
              </RadarChart>
            )}
          </>
        </ResponsiveContainer>
      </div>
      
      <div className="-mt-4">
        <button type="button" className="btn btn-xs btn-success animate-none text-white mb-2" onClick={handleExport}><FontAwesomeIcon icon={faFileExcel} /> Download to Excel</button>
      </div>
    </div>
  );
}

export default ChartRenderer;